import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Flame, ChevronDown, ChevronUp } from 'lucide-react';

/**
 * SectorHeatmap — laps × sectors colour matrix per driver.
 *
 * For every driver we show one row per lap (or all sector tuples we know
 * about) with one cell per sector. Each cell is coloured according to how
 * fast that sector was *relative to the field's best for the same sector*:
 *
 *   - LILA   (purple)  → fastest in the field for that sector ("morado")
 *   - VERDE             → personal best for the driver (their own best in
 *                         the session, but slower than the field leader)
 *   - AMARILLO          → competitive but not their own best (within ~2%
 *                         of their personal best)
 *   - ROJO              → off-pace (> 2% above their personal best, or
 *                         a clearly bad lap)
 *
 * The colour rule matches the Formula 1 "purple/green/yellow" convention
 * used by engineers world-wide. Red is our addition for off-pace laps.
 */

const PURPLE = '#a855f7';
const GREEN  = '#22c55e';
const YELLOW = '#eab308';
const RED    = '#ef4444';
const NEUTRAL = '#1f1f1f';

function fmtSector(s) {
  if (!s || s <= 0) return '—';
  return s.toFixed(3);
}

function fmtLap(s) {
  if (!s || s <= 0) return '—';
  const m = Math.floor(s / 60);
  const sec = (s % 60).toFixed(3);
  return m > 0 ? `${m}:${sec.padStart(6, '0')}` : sec;
}

export default function SectorHeatmap({ race }) {
  const [open, setOpen] = useState(true);
  const [showAll, setShowAll] = useState(false);

  // Step 1: figure out how many sectors and the field's best per sector.
  const { numSectors, fieldBestPerSector } = useMemo(() => {
    const drivers = race?.drivers || [];
    let max = 0;
    const best = {};   // {sector_number: time}
    for (const d of drivers) {
      for (const s of (d.sector_times || [])) {
        max = Math.max(max, s.sector_number);
        for (const t of (s.times || [])) {
          if (t > 0 && (best[s.sector_number] == null || t < best[s.sector_number])) {
            best[s.sector_number] = t;
          }
        }
      }
    }
    return { numSectors: max, fieldBestPerSector: best };
  }, [race]);

  // Step 2: build a table of [driver, laps[lap_idx][sector_idx]] using the
  // existing `sector_times` array. Same lap index across all sectors are
  // assumed to belong to the same physical lap (matches how the backend
  // already aligns them when parsing the analysis file).
  const rows = useMemo(() => {
    const drivers = race?.drivers || [];
    return drivers
      .map((d) => {
        const lapCount = Math.max(
          0,
          ...((d.sector_times || []).map((s) => (s.times || []).length)),
        );
        if (lapCount === 0) return null;

        // driver personal best per sector
        const personalBest = {};
        for (const s of (d.sector_times || [])) {
          const valid = (s.times || []).filter((t) => t > 0);
          if (valid.length) personalBest[s.sector_number] = Math.min(...valid);
        }

        // Per-lap sector times + lap total
        const laps = [];
        for (let li = 0; li < lapCount; li++) {
          const sectors = [];
          let lap_total = 0, lap_valid = true;
          for (let si = 1; si <= numSectors; si++) {
            const sec = (d.sector_times || []).find((s) => s.sector_number === si);
            const t = sec?.times?.[li];
            sectors.push(t || null);
            if (!t || t <= 0) lap_valid = false;
            else lap_total += t;
          }
          laps.push({
            sectors,
            lap_total: lap_valid ? lap_total : null,
          });
        }
        return { driver: d, personalBest, laps };
      })
      .filter(Boolean)
      .sort((a, b) => {
        const aBL = a.driver.best_lap || Infinity;
        const bBL = b.driver.best_lap || Infinity;
        return aBL - bBL;
      });
  }, [race, numSectors]);

  const cellColor = (t, sectorNum, personalBest) => {
    if (!t || t <= 0) return NEUTRAL;
    const fieldBest = fieldBestPerSector[sectorNum];
    const pBest = personalBest[sectorNum];
    // Within 5ms of the field best → purple (allow for float rounding).
    if (fieldBest != null && t <= fieldBest + 0.005) return PURPLE;
    // Within 5ms of personal best → green (their own best).
    if (pBest != null && t <= pBest + 0.005) return GREEN;
    // Within +2% of personal best → yellow (competitive lap).
    if (pBest != null && t <= pBest * 1.02) return YELLOW;
    return RED;
  };

  if (!rows.length) return null;

  // By default show top 8 drivers; "Mostrar todos" opens the rest.
  const visibleRows = showAll ? rows : rows.slice(0, 8);

  return (
    <Card className="bg-neutral-900 border-purple-700/40" data-testid="sector-heatmap">
      <CardHeader className="pb-2 cursor-pointer" onClick={() => setOpen((v) => !v)}>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg text-white flex items-center gap-2">
              <Flame className="w-5 h-5 text-purple-400" />
              Heatmap de Parciales — vuelta × sector
            </CardTitle>
            <CardDescription className="text-gray-500 text-xs mt-1">
              <span className="inline-block w-2.5 h-2.5 align-middle mr-1 rounded-sm" style={{ background: PURPLE }} /> Mejor del field
              {'  '}
              <span className="inline-block w-2.5 h-2.5 align-middle mx-1 ml-3 rounded-sm" style={{ background: GREEN }} /> Mejor personal
              {'  '}
              <span className="inline-block w-2.5 h-2.5 align-middle mx-1 ml-3 rounded-sm" style={{ background: YELLOW }} /> Competitiva (≤+2%)
              {'  '}
              <span className="inline-block w-2.5 h-2.5 align-middle mx-1 ml-3 rounded-sm" style={{ background: RED }} /> Off-pace
            </CardDescription>
          </div>
          <button type="button" className="text-gray-500 hover:text-white" data-testid="heatmap-toggle">
            {open ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </button>
        </div>
      </CardHeader>
      {open && (
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-neutral-900 z-10">
                <tr className="border-b border-neutral-700 bg-neutral-800/50">
                  <th className="text-left py-2 px-2 text-gray-400 font-medium">#</th>
                  <th className="text-left py-2 px-2 text-gray-400 font-medium min-w-[140px]">Piloto</th>
                  <th className="text-center py-2 px-2 text-gray-400 font-medium">Vta</th>
                  {Array.from({ length: numSectors }).map((_, i) => (
                    <th key={i} className="text-center py-2 px-2 text-gray-400 font-medium min-w-[58px]">
                      S{i + 1}
                    </th>
                  ))}
                  <th className="text-right py-2 px-3 text-gray-400 font-medium">Vuelta</th>
                </tr>
              </thead>
              <tbody>
                {visibleRows.map((row, ri) => (
                  <React.Fragment key={row.driver.driver_number}>
                    {row.laps.map((lap, li) => (
                      <tr
                        key={`${row.driver.driver_number}-${li}`}
                        className={`border-b border-neutral-900 hover:bg-neutral-800/40 ${li === 0 ? 'border-t border-neutral-700' : ''}`}
                      >
                        {li === 0 ? (
                          <>
                            <td
                              rowSpan={row.laps.length}
                              className="text-center py-1 px-2 text-gray-500 align-top pt-2 font-semibold"
                            >
                              {ri + 1}
                            </td>
                            <td
                              rowSpan={row.laps.length}
                              className="text-left py-1 px-2 text-white align-top pt-2"
                            >
                              <div className="font-semibold truncate" title={row.driver.driver_name}>
                                #{row.driver.driver_number} {row.driver.driver_name}
                              </div>
                              <div className="text-[10px] text-gray-500">
                                Mejor: {fmtLap(row.driver.best_lap)}
                              </div>
                            </td>
                          </>
                        ) : null}
                        <td className="text-center py-1 px-2 text-gray-500 font-mono">{li + 1}</td>
                        {lap.sectors.map((t, si) => {
                          const color = cellColor(t, si + 1, row.personalBest);
                          const isDarkText = color === YELLOW;
                          return (
                            <td
                              key={si}
                              className="text-center py-1 px-1 font-mono"
                              data-testid={`heat-cell-${row.driver.driver_number}-${li}-${si}`}
                            >
                              <span
                                className="inline-block w-full rounded px-1 py-1"
                                style={{
                                  background: color,
                                  color: color === NEUTRAL ? '#9ca3af' : (isDarkText ? '#0a0a0a' : '#ffffff'),
                                  fontWeight: color === PURPLE || color === GREEN ? 700 : 500,
                                  fontSize: 11,
                                  letterSpacing: '0.01em',
                                }}
                              >
                                {fmtSector(t)}
                              </span>
                            </td>
                          );
                        })}
                        <td className="text-right py-1 px-3 font-mono text-gray-200">
                          {fmtLap(lap.lap_total)}
                        </td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
          {rows.length > 8 && (
            <div className="text-center py-2 border-t border-neutral-800">
              <button
                type="button"
                onClick={() => setShowAll((v) => !v)}
                data-testid="heatmap-show-all"
                className="text-xs text-purple-400 hover:text-purple-300"
              >
                {showAll
                  ? `Mostrar sólo top 8`
                  : `Mostrar todos los ${rows.length} pilotos`}
              </button>
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
}
